import React, { useEffect } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Box from '@material-ui/core/Box';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';

export interface TabDatas {
    label: string;
    children: JSX.Element;
}
export interface TabsDatas {
    datas: TabDatas[];
    title?: string;
    subtitle?: string;
    loadAll?: boolean; // Allow to load all the tabs to avoid recreating it everytimes
    // When set, the selected tab index is persisted in sessionStorage under this key and
    // restored on mount (instead of resetting to 0), so navigating away and back keeps the tab.
    storageKey?: string;
}

export interface TabPanelProps {
    index: any;
    value: any;
    loadAll?: boolean;
}

const TabPanel: React.FC<TabPanelProps> = (props): JSX.Element => {
    const { children, value, index, loadAll, ...other } = props;

    const shouldLoad = loadAll || value === index;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {shouldLoad && <Box p={3}>{children}</Box>}
        </div>
    );
};

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
        fontSize: 14,
        fontWeight: 400,
        color: theme.palette.colors.table.text,

        '& .MuiTab-textColorInherit.Mui-selected': {
            color: '#2196F3',
        },
        '& .MuiTabs-indicator': {
            backgroundColor: '#2196F3',
        },
        '& [id^="simple-tabpanel"]': {
            backgroundColor: theme.palette.background.paper,
        },
    },
    tab: {
        flex: 1,
        maxWidth: 'none',
        borderBottom: '1px solid white',
        opacity: '1', // opacity for not selected tab
    },
    // tabs: {
    //     '& .MuiTab-textColorInherit.Mui-selected': {
    //         borderBottom: '1px solid white',
    //     },
    // },
}));

interface TabsProps extends WithTranslation {
    t: any;
    tabs: TabsDatas;
    onChange?: Function;
}

const FastTabs = (props: TabsProps): JSX.Element => {
    const classes = useStyles();
    const { tabs, onChange } = props;
    const path = window.location.pathname;
    const storageKey = tabs?.storageKey;

    const [value, setValue] = React.useState<number>(() => {
        if (storageKey) {
            const saved = parseInt(sessionStorage.getItem(storageKey) ?? '', 10);
            return Number.isNaN(saved) ? 0 : saved;
        }
        return 0;
    });

    const handleChange = (event, newValue): void => {
        setValue(newValue);
        if (storageKey) {
            sessionStorage.setItem(storageKey, String(newValue));
        }
        if (onChange) {
            onChange(newValue);
        }
    };

    useEffect(() => {
        // Reset the value to 0 when the path changes, unless the tab is persisted.
        if (!storageKey) {
            setValue(0);
        }
    }, [path, storageKey]);

    return (
        <div className={classes.root}>
            <AppBar color="transparent" position="static">
                <Tabs value={value} onChange={handleChange} aria-label="Tabs">
                    {tabs?.datas?.map((tab, i) => {
                        return <Tab key={`tabs-${i}`} className={classes.tab} label={tab.label} {...a11yProps(i)} />;
                    })}
                </Tabs>
            </AppBar>
            {tabs?.datas?.map((tab, i) => {
                return (
                    <TabPanel key={`tabPanel-${i}`} value={value} index={i} loadAll={tabs.loadAll}>
                        {tab.children}
                    </TabPanel>
                );
            })}
        </div>
    );
};

export default withTranslation(['tabs', 'common'])(FastTabs);
